import { BaseCanvasPack } from "./BaseCanvasPack";

export default class ForestCanvasPack extends BaseCanvasPack {
  drawBackground(
    context: CanvasRenderingContext2D,
    width: number,
    height: number,
    frameTime?: number,
    level?: number
  ): void {
    const gradient = context.createLinearGradient(0, 0, 0, height);
    gradient.addColorStop(0, "#1b4d3e");
    gradient.addColorStop(1, "#2e2716");
    context.fillStyle = gradient;
    context.fillRect(0, 0, width, height);
  }

  drawFrame(
    context: CanvasRenderingContext2D,
    width: number,
    height: number,
    frameTime?: number
  ): void {
    // Dark green tint
    context.fillStyle = "rgba(10, 30, 10, 0.4)";
    context.fillRect(0, 0, width, height);

    const branchColor = "#5D4037"; // Wood brown
    const barkColor = "#3E2723"; // Darker brown
    const thickness = 8;

    // Draw main branches (Frame)
    context.strokeStyle = branchColor;
    
    // Top Branch
    this.drawBranch(context, -5, 0, width + 5, 0, thickness, 100);
    // Bottom Branch
    this.drawBranch(context, -5, height, width + 5, height, thickness, 200);
    // Left Branch
    this.drawBranch(context, 0, -5, 0, height + 5, thickness, 300);
    // Right Branch
    this.drawBranch(context, width, -5, width, height + 5, thickness, 400);

    // Draw bark texture (thinner, darker lines overlaying the main branches)
    context.strokeStyle = barkColor;
    const barkThickness = 2;
    
    // Top Bark
    this.drawBranch(context, -5, -2, width + 5, -2, barkThickness, 105);
    // Bottom Bark
    this.drawBranch(context, -5, height + 2, width + 5, height + 2, barkThickness, 205);
    // Left Bark
    this.drawBranch(context, -2, -5, -2, height + 5, barkThickness, 305);
    // Right Bark
    this.drawBranch(context, width + 2, -5, width + 2, height + 5, barkThickness, 405);

    // Knots (circles on the wood)
    context.fillStyle = barkColor;
    this.drawKnot(context, width * 0.2, 0, 4);
    this.drawKnot(context, width, height * 0.7, 5);
    this.drawKnot(context, 0, height * 0.3, 3);
    this.drawKnot(context, width * 0.8, height, 4);

    // Ivy/Vines wrapping around the branches
    this.drawVines(context, width, height);
  }

  drawOverlay(
    context: CanvasRenderingContext2D,
    width: number,
    height: number,
    frameTime?: number
  ): void {
    if (frameTime !== undefined) {
      this.drawFallingLeaves(context, width, height, frameTime);
      this.drawGodRays(context, width, height, frameTime);
    }
  }

  /**
   * Draws a wobbly branch line between two points
   */
  private drawBranch(
    context: CanvasRenderingContext2D,
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    thickness: number,
    seedOffset: number
  ): void {
    const length = Math.hypot(x2 - x1, y2 - y1);
    const segments = Math.max(3, Math.floor(length / 15));
    
    context.lineWidth = thickness;
    context.lineCap = "round";
    context.lineJoin = "round";
    
    context.beginPath();
    context.moveTo(x1, y1);

    for (let i = 1; i < segments; i++) {
      const t = i / segments;
      const x = x1 + (x2 - x1) * t;
      const y = y1 + (y2 - y1) * t;
      
      // Deterministic noise for wobble
      const noise = Math.sin(i * 0.8 + seedOffset) * Math.cos(i * 0.3 + seedOffset * 2) * (thickness * 0.4);
      
      // Determine perpendicular direction
      let px = -(y2 - y1);
      let py = (x2 - x1);
      const len = Math.hypot(px, py);
      if (len > 0) {
        px /= len;
        py /= len;
      }
      
      context.lineTo(x + px * noise, y + py * noise);
    }
    context.lineTo(x2, y2);
    context.stroke();
  }

  private drawKnot(context: CanvasRenderingContext2D, x: number, y: number, radius: number): void {
    context.beginPath();
    context.ellipse(x, y, radius, radius * 0.6, Math.random(), 0, Math.PI * 2);
    context.fill();
  }

  private drawVines(context: CanvasRenderingContext2D, width: number, height: number): void {
    context.save();
    context.strokeStyle = "#2E7D32";
    context.lineWidth = 3;
    context.lineCap = "round";
    
    // Top left corner vine
    context.beginPath();
    context.moveTo(-5, 20);
    context.bezierCurveTo(5, 15, 15, 5, 20, -5);
    context.stroke();
    
    // Top right corner vine
    context.beginPath();
    context.moveTo(width + 5, 20);
    context.bezierCurveTo(width - 5, 15, width - 15, 5, width - 20, -5);
    context.stroke();

    // Bottom left corner vine
    context.beginPath();
    context.moveTo(-5, height - 20);
    context.bezierCurveTo(5, height - 15, 15, height - 5, 20, height + 5);
    context.stroke();

    // Bottom right corner vine
    context.beginPath();
    context.moveTo(width + 5, height - 20);
    context.bezierCurveTo(width - 5, height - 15, width - 15, height - 5, width - 20, height + 5);
    context.stroke();
    
    // Leaves on vines
    context.fillStyle = "#4CAF50";
    const drawLeaf = (lx: number, ly: number, angle: number) => {
        context.beginPath();
        context.ellipse(lx, ly, 4, 2, angle, 0, Math.PI * 2);
        context.fill();
    };

    drawLeaf(10, 10, Math.PI / 4);
    drawLeaf(width - 10, 10, -Math.PI / 4);
    drawLeaf(10, height - 10, -Math.PI / 4);
    drawLeaf(width - 10, height - 10, Math.PI / 4);
    
    context.restore();
  }

  private drawFallingLeaves(
    context: CanvasRenderingContext2D,
    width: number,
    height: number,
    frameTime: number
  ): void {
    context.save();
    
    const count = 15;
    for (let i = 0; i < count; i++) {
      const seed = i * 56.78;
      const speed = 0.05 + (Math.sin(seed) * 0.02);
      
      const x = (Math.sin(seed * 10) * width + Math.sin(frameTime * 0.001 + seed) * 50 + width) % width;
      const y = (frameTime * speed + seed * 100) % (height + 50) - 20;
      
      const rotation = frameTime * 0.002 + seed;
      
      context.translate(x, y);
      context.rotate(rotation);
      
      context.fillStyle = i % 2 === 0 ? "#81C784" : "#FFCC80"; // Green or Autumn Orange
      context.beginPath();
      context.ellipse(0, 0, 6, 3, 0, 0, Math.PI * 2);
      context.fill();
      
      context.rotate(-rotation);
      context.translate(-x, -y);
    }
    
    context.restore();
  }

  private drawGodRays(
    context: CanvasRenderingContext2D,
    width: number,
    height: number,
    frameTime: number
  ): void {
    context.save();
    context.globalCompositeOperation = "overlay";
    
    const rayCount = 3;
    for (let i = 0; i < rayCount; i++) {
      const angle = Math.PI / 4; // 45 degrees
      const offset = (frameTime * 0.02 + i * 200) % (width * 2);
      
      const gradient = context.createLinearGradient(0, 0, width, height);
      gradient.addColorStop(0, "rgba(255, 255, 200, 0)");
      gradient.addColorStop(0.5, "rgba(255, 255, 200, 0.1)");
      gradient.addColorStop(1, "rgba(255, 255, 200, 0)");
      
      context.fillStyle = gradient;
      context.translate(offset - width, 0);
      context.rotate(angle);
      context.fillRect(0, -100, 100, height * 2);
      context.rotate(-angle);
      context.translate(-(offset - width), 0);
    }
    
    context.restore();
  }

  getBorderStyle(): string {
    return "none"; // Canvas draws the border now
  }

  getBoxShadow(frameTime?: number): string {
    return "0 0 20px rgba(46, 125, 50, 0.5)";
  }

  getBackdropFilter(): string {
    return "blur(2px) sepia(0.2)";
  }
}
